import { Controller } from '@hotwired/stimulus';
import { Calendar } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import NavigationHelperController from "./navigation_helper_controller";
import CalendarEventsManagementService from "./calendar_events/events_management_service";
import { longFormatDate, calculateWeeksBetween } from "../utils/dates";
import * as Turbo from '@hotwired/turbo';
import dayjs from 'dayjs';
import usHolidaysData from '../../assets/data/us-holidays.json';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import Hammer from 'hammerjs';
import emptyListImg from '../images/icons/empty-list.svg';

dayjs.extend(utc);
dayjs.extend(timezone);

const MOBILE_MAX_WIDTH = 768;
const DEFAULT_MONTH_NUMBER = 5;
const DEFAULT_DAY_NUMBER = 1;
const COLORS = ['blue-400', 'orange-600', 'pink-500', 'pink-900', 'purple-500', 'green-700', 'red-400', 'green-900', 'yellow-800', 'blue-800'];
const LIGHT_COLORS = ['blue-100', 'orange-100', 'pink-100', 'pink-200', 'purple-100', 'green-100', 'red-50', 'green-200', 'yellow-100', 'blue-100'];

export default class extends Controller {
  static targets = ['calendar', 'daysNavigationContainer', 'summerPopup', 'confettiAnimation', 'viewSelector'];
  static values = { excludedAssignables: { type: String, default: '' }, timezone: { type: String, default: 'UTC' } };
  kids = [];
  eventColor = { personal: { dark: COLORS[0], light: LIGHT_COLORS[0] } };
  showEventConfettiAnimation;

  async connect() {
    // Work around for dynamic history state with queryparams not updating with Turbo history
    Turbo.session.drive = false;
    await this.#getUserKids();
    this.#setKidsColors();
    this.#setUpWelcomePopup();
    this.calendarEventManagement = new CalendarEventsManagementService(this.excludedAssignablesValue ? this.excludedAssignablesValue.split(',') : []);
    this.calendar = new Calendar(this.calendarTarget, {
      timeZone: this.timezoneValue || 'UTC',
      plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin],
      initialView: window.innerWidth < MOBILE_MAX_WIDTH ? 'timeGridDay' : 'dayGridMonth',
      initialDate: this.#getInitialDate(),
      events: this.#fetchCalendarEvents.bind(this),
      eventOrder: 'priority',
      dayHeaderFormat: {
        weekday: 'long'
      },
      headerToolbar: {
        start: 'prevMonth,prev',
        center: 'title',
        end: 'next,nextMonth'
      },
      eventContent: this.#renderEventContent.bind(this),
      noEventsContent: {
        html: `
          <div class="text-center text-gray-500 px-4 py-6">
            <img src=${emptyListImg} alt="No events" class="mx-auto mb-4 w-28 h-28">
            <p class="text-24 text-blue-400 font-semibold">Looks like there are no plans yet</p>
            <p class="text-14 text-gray-500 mt-4">
              Let's find something fun for this week
            </p>
            <div class="mt-6 flex justify-center gap-4 flex-wrap">
              <a href="/adventure-guide" rel="noopener noreferrer" target="_blank" class="btn btn-md btn-blue w-full md:w-auto">Ask the Agent</a>
              <a href="/" rel="noopener noreferrer" class="btn btn-md btn-primary w-full md:w-auto">Explore activitites</a>
            </div>
          </div>`
      },
      customButtons: {
        prevMonth: {
          text: 'PreviousMonth',
          click: () => { this.#changeMonth(-1); },
          icon: 'chevrons-left'
        },
        nextMonth: {
          text: 'Next month',
          click: () => { this.#changeMonth(1); },
          icon: 'chevrons-right'
        }
      },
      slotLabelFormat: {
        hour: '2-digit',
        minute: '2-digit',
        meridiem: 'narrow',
        hour12: false
      },
      datesSet: this.#updateCustomDateButtons.bind(this),
      eventTimeFormat: {
        hour: 'numeric',
        minute: '2-digit',
        meridiem: true
      },
      displayEventEnd: true,
      eventClick: this.#handleEventClick,
      dateClick: this.handleDateClick.bind(this)
    });

    this.#handleCalendarViewClass(this.calendar.view.type);
    this.#setUpViewSelector(this.calendar.view.type);
    this.calendar.on('datesSet', () => this.calendarEventManagement.syncCalendarDates(this.calendar));
    this.calendar.render();
    this.#setUpSwipeInteraction();
  }

  #setUpSwipeInteraction() {
    if (!this.hasCalendarTarget || !this.calendarTarget.parentNode || !this.calendar) return;
    this.hammerManager = new Hammer(this.calendarTarget.parentNode);

    this.hammerManager.on('swipeleft', () => {
      this.calendar.next();
    });

    this.hammerManager.on('swiperight', () => {
      this.calendar.prev();
    });
  }

  changeCalendarView({ target }) {
    const newView = target.value;
    if (!newView) return;
  
    this.calendar.changeView(newView);
  
    if (!this.hasDaysNavigationContainerTarget) return;
  
    const shouldShowDaysNavigation = newView === 'timeGridDay';
    this.daysNavigationContainerTarget.classList.toggle('hidden', !shouldShowDaysNavigation);

    this.#handleCalendarViewClass(newView);
  }

  #setUpViewSelector(view) {
    if (!this.hasViewSelectorTarget) return;
    this.viewSelectorTarget.value = view;
  }

  #handleCalendarViewClass(view) {
    this.calendarTarget.classList.remove(
      'view-timeGridDay',
      'view-dayGridMonth',
      'view-listWeek'
    );

    this.calendarTarget.classList.add(`view-${view}`);
  }

  addEvent(body) {
    this.calendar.addEvent(body);
  }

  removeEvent(eventId) {
    const event = this.calendar.getEventById(eventId);
    if (event) event.remove();
  }

  disconnect() {
    this.calendar.destroy();
    if (this.hammerManager) {
      this.hammerManager.off("swipeleft");
      this.hammerManager.off("swiperight");
      this.hammerManager.destroy();
    }
    Turbo.session.drive = true;
  }

  // Event Creation Logic Start
  handleDateClick(info) {
    this.#renderNewEventForm(encodeURIComponent(info.dateStr));
  }

  createNewEvent() {
    this.#renderNewEventForm(encodeURIComponent(this.#getCurrentVisibleDate()));
  }

  #renderNewEventForm(customDate) {
    const path = `/calendar_events/new?date=${customDate}`;
    Turbo.visit(path, {
      frame: 'new-event-form'
    });
  }

  #getCurrentVisibleDate() {
    const currentDate = this.calendar.view.currentStart;
    const isMonthView = this.calendar.view.type === 'dayGridMonth';
  
    return !isMonthView ? currentDate.toISOString().split('T')[0] : new Date().toISOString();
  }
  // Event Creation Logic End

  #setKidsColors() {
    this.kids.forEach((kid, index) => {
      const colorIdx = (index + 1) % COLORS.length;
      this.eventColor[kid.id] = { dark: COLORS[colorIdx], light: LIGHT_COLORS[colorIdx] };
    });
  }

  #getInitialDate() {
    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.get('start_date')) {
      return urlParams.get('start_date');
    } else {
      const defaultDate = new Date(Date.UTC((new Date()).getFullYear(), DEFAULT_MONTH_NUMBER, DEFAULT_DAY_NUMBER));
      return defaultDate;
    }
  }

  #handleEventClick(info) {
    const isEditable = info.event.extendedProps.editable;
    if (isEditable) {
      Turbo.visit(`/calendar_events/${info.event.id}/edit`, { frame: 'new-event-form' });
    }
  }

  #renderEventContent(info) {
    const eventTitle = info.event.title;
    const isSchoolEvent = info.event.extendedProps?.context_type === 'School';
    const isUSHoliday = info.event.extendedProps?.context_type === 'Holiday';

    if (isSchoolEvent || isUSHoliday) {
      return { html: `<div class="fc-event-title">${eventTitle}</div>` }
    };

    const playAnimation = this.hasConfettiAnimationTarget && this.showEventConfettiAnimation === info.event.id;
    let animationElement;
    
    if (playAnimation) {
      animationElement = this.confettiAnimationTarget.cloneNode(true);
      animationElement.setAttribute('data-lottie-play-on-load-once', 'true');
      this.showEventConfettiAnimation = '';
    }

    const startTime = info.event.startStr ? dayjs(info.event.startStr).tz(this.timezoneValue).format('hh:mm A') : '';
    const endTime = info.event.endStr ? dayjs(info.event.endStr).tz(this.timezoneValue).format('hh:mm A') : '';

    return {
      html: `
        <div class="inline-block whitespace-normal">
          <span class="fc-icon inline"><i class="fa-solid fa-user"></i></span>
          ${info.event.extendedProps?.assignable_name ? `<span class="fc-event-username inline">${info.event.extendedProps.assignable_name}</span>` : ''}
          ${info.timeText ? `<span class="fc-event-time inline">${startTime.toLowerCase()} - ${endTime.toLowerCase()}: </span>` : ''}
          <span class="fc-event-title inline">${eventTitle}</span>
        </div>
        ${playAnimation && animationElement ? animationElement.outerHTML : ''}
      `
    };
  }

  /**
   * Custom day buttons for mobile day-grid calendar view
   */
  #updateCustomDateButtons(info) {
    const isDayView = info?.view?.type === 'timeGridDay';
    const currentDate = info?.startStr;
    if (!isDayView || !currentDate) return;

    let navigationsButtonContainer;

    if (this.hasDaysNavigationContainerTarget) {
      navigationsButtonContainer = this.daysNavigationContainerTarget;
      navigationsButtonContainer.innerHTML = '';
    } else {
      navigationsButtonContainer = document.createElement('div');
      navigationsButtonContainer.setAttribute('data-squad-planner-target', 'daysNavigationContainer');
      navigationsButtonContainer.classList.add('day-buttons-container');
    }
    
    const baseDate = dayjs(currentDate);
    
    for (let i = -3; i <= 3; i++) {
      const day = baseDate.add(i, 'day');
      
      const button = document.createElement('button');
      button.classList.add('day-button');

      button.innerHTML = `
        <span class="num">${day.format('D')}</span>
        <span class="day">${day.format('ddd')}</span>
      `;
      
      if (i === 0) {
        button.classList.add('active');
      }

      button.addEventListener('click', function() {
        this.calendar.gotoDate(day.format('YYYY-MM-DD'));
      }.bind(this));
      
      navigationsButtonContainer.appendChild(button)
    }

    if (!this.hasDaysNavigationContainerTarget) {
      const dayGridContainer = this.calendarTarget.querySelector('.fc-view-harness.fc-view-harness-active');
      this.calendarTarget.insertBefore(navigationsButtonContainer, dayGridContainer);
    }
  }
  
  #changeMonth(offset) {
    const currentDate = this.calendar.getDate();
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + offset);
    this.calendar.gotoDate(newDate);
  }

  async #fetchCalendarEvents(dateRange = {}) {
    const events = await this.calendarEventManagement.fetchEvents(dateRange);
    return [...this.#transformToFullCalendarEvents(events), ...usHolidaysData];
  }

  onAssignableSelected(e) {
    if (!this.calendarEventManagement) return [];
    const checkbox = e.target;
    this.calendarEventManagement.handleAssignableSelection(checkbox.value, checkbox.checked);

    this.calendar.refetchEvents();
  }

  async #getUserKids() {
    const csrfToken = document.querySelector("meta[name='csrf-token']").getAttribute('content');
    try {
      const response = await fetch('/user/visitors.json', {
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        }
      });
  
      const data = await response.json();
  
      this.kids = data || [];
    } catch (error) {
      console.log(error);
    }
  }

  refetchEvents (event) {
    if (event.detail.success) {
      this.calendar.refetchEvents();
    }
  }

  refetchEditedEvents(event) {
    if (event.detail.success) {
      const eventStatus = event.detail.formSubmission.body.get('default_event[status]');
      const eventId = event.detail.formSubmission.body.get('default_event[id]');

      const updatedEvent = this.calendar.getEvents().find(event => event.id === eventId);

      const updatedToRegistered = updatedEvent && updatedEvent.extendedProps?.status !== eventStatus && eventStatus === 'registered';
      if (updatedToRegistered) this.showEventConfettiAnimation = eventId;

      this.calendar.refetchEvents();
    }
  }

  #setUpWelcomePopup() {
    if (!this.hasSummerPopupTarget) return;

    const schoolDateRage = this.#getSchoolDateRange(this.kids);
    if (!schoolDateRage) return;
    const { latestLastDay, earliestFirstDay, weeksBetween } = schoolDateRage;

    const placeholders = this.summerPopupTarget.querySelectorAll("[data-fill='true']");

    if (placeholders.length >= 3) {
      const [summerWeeks, summerStart, summerEnd] = placeholders;

      const [latestM, latestD] = latestLastDay.toUpperCase().split(' ');
      const [earliestM, earliestD] = earliestFirstDay.toUpperCase().split(' ');

      summerStart.innerHTML = `<span>${latestD}</span> <span>${latestM}</span>`;
      summerEnd.innerHTML = `<span>${earliestD}</span> <span>${earliestM}</span>`;
      summerWeeks.innerHTML = `${weeksBetween} weeks`;
    }

    this.summerPopupTarget.classList.remove('bottom-[-100%]');
    const navHelper = new NavigationHelperController();
    navHelper.removeParams();
  }

  /**
   * 
   * @param {*} kids kids data with their school dates
   * @returns and object with:
   * the earliest first_day_of_school,
   * the latest last_Day_of_school
   * and number of weeks in between
   */
  #getSchoolDateRange(kids) {
    if (!kids.length) return null;

    let earliestFirstDay = null;
    let latestLastDay = null;

    kids.forEach(kid => {
      const firstDay = new Date(kid.first_day_of_school);
      const lastDay = new Date(kid.last_day_of_school);

      if (kid.first_day_of_school && !isNaN(firstDay) && (!earliestFirstDay || firstDay < earliestFirstDay)) {
        earliestFirstDay = firstDay;
      }

      if (kid.last_day_of_school && !isNaN(lastDay) && (!latestLastDay || lastDay > latestLastDay)) {
        latestLastDay = lastDay;
      }
    });

    if (!latestLastDay || !earliestFirstDay) return null;

    const weeksBetween = calculateWeeksBetween(latestLastDay, earliestFirstDay);

    return {
      earliestFirstDay: longFormatDate(earliestFirstDay, this.timezoneValue),
      latestLastDay: longFormatDate(latestLastDay, this.timezoneValue),
      weeksBetween
    };
  }

  #isSchoolEvent(event) {
    const schoolEventsTitle = ['Last Day of School', 'First Day of School'];
    return event.context_type === 'School' && schoolEventsTitle.includes(event.title) && event.assignable?.first_name;
  }

  #eventParser(event, fixedProps = {}) {
    const eventIsPersonal = event.assignable_type === 'User';
    const customColor = this.eventColor[event.assignable.id] || this.eventColor.personal;
    return {
      id: event.id,
      title: event.title,
      start: event.start_date,
      end: event.end_date,
      description: event.notes,
      allDay: event.start_date === event.end_date,
      classNames: [`text-${customColor.dark}`, event.status === 'planned' ? `squad-planner-planned-event bg-${customColor.light}` : `squad-planner-registered-event bg-${customColor.dark}`],
      ...fixedProps,
      extendedProps: {
        assignable_name: eventIsPersonal ? 'Personal' : event.assignable?.first_name,
        context_type: event.context_type,
        status: event.status,
        ...fixedProps.extendedProps
      }
    }
  }

  /**
   * @param {*} events data returned from the API
   * @returns events parsed into a format suitable for FullCalendar
   */
  #transformToFullCalendarEvents(events) {
    const isMonthView = this.calendar.view.type === 'dayGridMonth';
    const groupedSchoolEvents = new Map();
    const parsedEvents = [];

    events.forEach(event => {
      if (this.#isSchoolEvent(event)) {
        const key = `${event.title}-${event.start_date}`;
        const assignableName = event.assignable.first_name;
        
        if (!groupedSchoolEvents.has(key)) {
          groupedSchoolEvents.set(key, { 
            ...event, 
            assignables: [assignableName] 
          });
        } else {
          const groupedEvent = groupedSchoolEvents.get(key);
          if (!groupedEvent.assignables.includes(assignableName)) {
            groupedEvent.assignables.push(assignableName);
          }
        }
      } else {
        parsedEvents.push(this.#eventParser(event, { extendedProps: { editable: true } }));
      }
    });

    groupedSchoolEvents.forEach((event, key) => {
      const assignableNames = event.assignables.join(', ');
      parsedEvents.push(this.#eventParser(event, {
        id: key,
        title: `${assignableNames} - ${event.title}`,
        allDay: true,
        classNames: ['fc-school-event']
      }));
    });

    return parsedEvents;
  }
}
